// === DIALOGUES DES SURVIVANTS (Sprint A3) ===
// Lignes contextuelles affichées en speech bubble au clic sur un survivant
// dans IslandCampScene. Une ligne par survivant et par jour calendaire (rotation
// déterministe via hash de l'ID + day index).
//
// Garder court (max ~60 caractères) pour tenir dans une bulle 200px de large.
// Ton : pirate, fataliste, occasionnellement humoristique. Pas de spoilers majeurs.

import type { IslandSurvivorClass } from '../rendering/IslandTileRenderer';

const WARRIOR_LINES: readonly string[] = [
  'Que la mer m\'avale si je recule encore.',
  'Mon sabre a soif, capitaine.',
  'Les morts ne se plaignent pas. Pratique.',
  'Tu as vu ce que j\'ai fait au dernier boss ? Ça compte.',
  'Une bataille de plus, une dent de moins.',
  'Je dors mal. Trop d\'os qui claquent dans la nuit.',
  'L\'enclume cogne fort à l\'atelier. J\'aime ça.',
  'Si on me tue, vole-moi un rhum à la taverne.',
  'Donne-moi une cible, capitaine. N\'importe laquelle.',
  'J\'ai compté mes cicatrices. Plus assez de doigts.',
];

const RANGER_LINES: readonly string[] = [
  'Le vent vient du nord. Mauvais signe.',
  'J\'ai vu une voile à l\'horizon. Peut-être un mirage.',
  'Mes flèches m\'ont coûté trois doublons. Honteux.',
  'Silence. J\'entends la jungle bouger.',
  'Une mouette est tombée morte du ciel ce matin.',
  'Je tire mieux quand j\'ai faim. Bizarre, hein ?',
  'L\'arc est vivant. Le sabre est juste fier.',
  'Tu chasses, capitaine ? Je t\'apprends si tu veux.',
  'Pas de bruit. Les esprits écoutent au crépuscule.',
  'Si je rentre pas, c\'est qu\'un truc m\'aura mangé.',
];

const MEDIC_LINES: readonly string[] = [
  'Tu saignes encore, capitaine. C\'est de famille ?',
  'Trois doigts à recoudre cette semaine. Sale record.',
  'J\'ai presque plus de bandages. Pense à raider.',
  'Le rhum désinfecte aussi. Souviens-toi.',
  'Mes morts m\'ont appris plus que mes vivants.',
  'Garde tes plaies au sec. C\'est gratuit comme conseil.',
  'Si tu meurs là-bas, je facture pas la dernière potion.',
  'L\'autre nuit, j\'ai rêvé du vieux Capitaine Jack.',
  'Le sel des plages, ça brûle comme l\'enfer.',
  'Je crois que je vais ouvrir une école d\'amputés.',
];

const DIALOGUES: Record<IslandSurvivorClass, readonly string[]> = {
  warrior: WARRIOR_LINES,
  ranger: RANGER_LINES,
  medic: MEDIC_LINES,
};

/** Index du jour calendaire courant (UTC, change à minuit). */
export function dayIndex(now: number = Date.now()): number {
  return Math.floor(now / 86_400_000);
}

/**
 * Renvoie la ligne du jour pour un survivant donné. Rotation déterministe :
 * même survivant, même jour → même ligne. Change à minuit UTC.
 */
export function pickDialogue(
  survivorId: string,
  cls: IslandSurvivorClass,
  now: number = Date.now(),
): string {
  const pool = DIALOGUES[cls];
  if (pool.length === 0) return '...';
  const day = dayIndex(now);
  // Hash simple sur la chaîne + day index pour rotation pseudo-aléatoire.
  let h = day | 0;
  for (let i = 0; i < survivorId.length; i++) {
    h = ((h << 5) - h + survivorId.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % pool.length;
  return pool[idx];
}
